// Sprint 0.1 — Coda locale ordini (Beach Menu PRD v1.1).
//
// Obiettivo: nessun ordine perso quando la rete è ballerina (WiFi spiaggia,
// tende metalliche, celle piene). La coda vive in localStorage — soluzione
// più semplice, sincrona, sufficiente per code < 20 ordini. Non passiamo
// a IndexedDB fino a esigenza reale documentata (YAGNI, PRD § 0.1).
//
// Idempotenza: ogni payload contiene già `idempotency_key`. Il backend
// (`POST /public/orders`, routes/public.py:105) dedup su quella chiave —
// retry duplicati non creano ordini doppi. Blindatura verificata in
// Sprint 0.6 (test_sprint0_idempotency.py).

import { api } from "./api";

const KEY = "sb_order_queue_v1";
const MAX_QUEUE = 20;
const TTL_MS = 24 * 60 * 60 * 1000;         // 24h
const BACKOFF_MS = [1000, 2000, 5000, 15000, 60000];

const EVT_CHANGE = "sb-order-queue-change";
const EVT_FLUSHED = "sb-order-queue-flushed";
const EVT_REJECTED = "sb-order-queue-rejected";

function _read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    const now = Date.now();
    return arr.filter((e) => now - (e.enqueued_at || 0) < TTL_MS);
  } catch { return []; }
}

function _write(q) {
  try {
    localStorage.setItem(KEY, JSON.stringify(q.slice(-MAX_QUEUE)));
    window.dispatchEvent(new CustomEvent(EVT_CHANGE));
  } catch { /* quota / storage bloccato → fallback silenzioso */ }
}

export function queueSize() {
  return _read().length;
}

/**
 * Aggiunge un ordine alla coda per replay in background.
 * Ritorna l'entry (id locale usato per correlare gli eventi).
 */
export function enqueueOrder(payload) {
  const entry = {
    id: crypto.randomUUID(),
    payload,
    enqueued_at: Date.now(),
    attempts: 0,
  };
  const q = _read();
  q.push(entry);
  _write(q);
  setTimeout(() => drainQueue(), 100);
  return entry;
}

function _remove(entryId) {
  _write(_read().filter((e) => e.id !== entryId));
}

function _bumpAttempts(entryId) {
  _write(_read().map((e) => (e.id === entryId ? { ...e, attempts: (e.attempts || 0) + 1 } : e)));
}

let _draining = false;

/**
 * Tenta lo smaltimento sequenziale della coda con backoff esponenziale.
 * - Successo → rimozione + evento `sb-order-queue-flushed` (con orderId reale)
 * - Errore di rete → backoff, resta in coda
 * - Errore server (4xx/5xx applicativo) → rimozione + evento `sb-order-queue-rejected`
 */
export async function drainQueue() {
  if (_draining) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  _draining = true;
  try {
    while (true) {
      const q = _read();
      if (q.length === 0) break;
      const entry = q[0];
      try {
        const { data } = await api.post("/public/orders", entry.payload);
        _remove(entry.id);
        window.dispatchEvent(new CustomEvent(EVT_FLUSHED, {
          detail: { entryId: entry.id, orderId: data?.id, code: data?.code, duplicate: !!data?.duplicate },
        }));
      } catch (err) {
        const isNetwork = !err?.response;
        if (!isNetwork) {
          // Errore applicativo (validazione, 409, 4xx) → non retryare all'infinito
          _remove(entry.id);
          window.dispatchEvent(new CustomEvent(EVT_REJECTED, {
            detail: { entryId: entry.id, error: err?.response?.data?.detail || "Ordine rifiutato" },
          }));
          continue;
        }
        _bumpAttempts(entry.id);
        const current = _read().find((e) => e.id === entry.id);
        const attempts = current?.attempts || 0;
        if (attempts >= BACKOFF_MS.length) {
          // Rete morta a lungo → esci, ritenteremo quando torna 'online'
          break;
        }
        await new Promise((r) => setTimeout(r, BACKOFF_MS[attempts - 1]));
      }
    }
  } finally {
    _draining = false;
  }
}

// Auto-drain: quando la rete torna online + drain iniziale al caricamento pagina.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => drainQueue());
  setTimeout(() => drainQueue(), 500);
}
